import { useEffect, useId, useRef, useState, type DragEvent } from "react";
import { ImagePlus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ImageDropzoneProps {
  /** A imagem que já existe (URL salva), mostrada quando não há arquivo novo. */
  currentUrl?: string | null;
  /** O arquivo escolhido e ainda não enviado. */
  file: File | null;
  onFileChange: (file: File | null) => void;
  /** Remove a imagem atual (só aparece quando há uma). */
  onRemoveCurrent?: () => void;
  accept: string[];
  maxBytes: number;
  disabled?: boolean;
  /** Fração de 0 a 1 enquanto o envio acontece. */
  progress?: number | null;
  /** Mensagem quando o arquivo é recusado pela validação local. */
  onReject?: (message: string) => void;
  label?: string;
}

/**
 * Campo de imagem: arrastar o arquivo ou clicar para escolher, com a prévia do
 * que está salvo ou do que vai ser enviado.
 *
 * É um primitivo sem domínio: não sabe o que é categoria nem para onde a imagem
 * vai. Quem envia é a tela, quando salva. Escolher um arquivo aqui não grava
 * nada, e cancelar o diálogo descarta a escolha.
 */
export function ImageDropzone({
  currentUrl,
  file,
  onFileChange,
  onRemoveCurrent,
  accept,
  maxBytes,
  disabled,
  progress,
  onReject,
  label = "Imagem",
}: ImageDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  // A prévia do arquivo novo é um object URL, que precisa ser liberado: sem
  // isso cada troca de imagem deixaria a anterior presa na memória da aba.
  // O estado vem do efeito de propósito: o object URL é recurso de fora do
  // React, criado e liberado junto com o arquivo, e no StrictMode um useMemo
  // o liberaria na remontagem de teste sem criar outro.
  useEffect(() => {
    if (!file) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function pickFile(candidate: File | undefined) {
    if (!candidate) return;
    if (!accept.includes(candidate.type)) {
      onReject?.("Use uma imagem JPG, PNG ou WebP.");
      return;
    }
    if (candidate.size > maxBytes) {
      onReject?.(`A imagem passa de ${Math.round(maxBytes / (1024 * 1024))} MB. Escolha uma menor.`);
      return;
    }
    onFileChange(candidate);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    pickFile(event.dataTransfer.files?.[0]);
  }

  const shown = preview ?? currentUrl ?? null;
  const uploading = progress != null;

  return (
    <div className="space-y-1.5">
      <label htmlFor={inputId} className="text-small font-semibold">
        {label}
      </label>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "relative flex flex-col items-center gap-3 rounded-md border-2 border-dashed p-3 text-center transition-colors sm:flex-row sm:text-left",
          dragging ? "border-primary bg-primary/10" : "border-border",
          disabled && "opacity-60",
        )}
      >
        <div className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
          {shown ? (
            <img src={shown} alt="Prévia da imagem" className="size-full object-cover" />
          ) : (
            <ImagePlus className="size-7 text-muted-foreground" aria-hidden />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-small">
            {file
              ? `Nova imagem: ${file.name}`
              : shown
                ? "Imagem atual. Arraste outra para cá para trocar."
                : "Arraste uma imagem para cá, ou escolha um arquivo."}
          </p>
          <p className="text-caption text-muted-foreground">
            JPG, PNG ou WebP. Ela é reduzida antes do envio.
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
            >
              <Upload /> {shown ? "Trocar imagem" : "Escolher imagem"}
            </Button>
            {file ? (
              <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => onFileChange(null)}>
                Desfazer
              </Button>
            ) : currentUrl && onRemoveCurrent ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                disabled={disabled}
                onClick={onRemoveCurrent}
              >
                <Trash2 /> Remover
              </Button>
            ) : null}
          </div>
        </div>

        {uploading ? (
          <div className="absolute inset-x-3 bottom-2 h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${Math.round((progress ?? 0) * 100)}%` }}
            />
          </div>
        ) : null}

        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept.join(",")}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            pickFile(event.target.files?.[0]);
            // Permite escolher o mesmo arquivo de novo depois de desfazer.
            event.target.value = "";
          }}
        />
      </div>
    </div>
  );
}
